// Utility functions for match slug generation (similar to WordPress fs_sanitize_slug)

export function sanitizeSlug(str: string): string {
  if (!str) return ''
  
  // Convert to lowercase
  let slug = str.toLowerCase()
  
  // Extended character mapping including European characters
  const accents = {
    // Vietnamese
    'à': 'a', 'á': 'a', 'ạ': 'a', 'ả': 'a', 'ã': 'a', 'â': 'a', 'ầ': 'a', 'ấ': 'a', 'ậ': 'a', 'ẩ': 'a', 'ẫ': 'a', 'ă': 'a', 'ằ': 'a', 'ắ': 'a', 'ặ': 'a', 'ẳ': 'a', 'ẵ': 'a',
    'è': 'e', 'é': 'e', 'ẹ': 'e', 'ẻ': 'e', 'ẽ': 'e', 'ê': 'e', 'ề': 'e', 'ế': 'e', 'ệ': 'e', 'ể': 'e', 'ễ': 'e',
    'ì': 'i', 'í': 'i', 'ị': 'i', 'ỉ': 'i', 'ĩ': 'i',
    'ò': 'o', 'ó': 'o', 'ọ': 'o', 'ỏ': 'o', 'õ': 'o', 'ô': 'o', 'ồ': 'o', 'ố': 'o', 'ộ': 'o', 'ổ': 'o', 'ỗ': 'o', 'ơ': 'o', 'ờ': 'o', 'ớ': 'o', 'ợ': 'o', 'ở': 'o', 'ỡ': 'o',
    'ù': 'u', 'ú': 'u', 'ụ': 'u', 'ủ': 'u', 'ũ': 'u', 'ư': 'u', 'ừ': 'u', 'ứ': 'u', 'ự': 'u', 'ử': 'u', 'ữ': 'u',
    'ỳ': 'y', 'ý': 'y', 'ỵ': 'y', 'ỷ': 'y', 'ỹ': 'y',
    'đ': 'd',
    
    // European characters (Finnish, German, Swedish, etc.) - avoiding duplicates
    'ä': 'a', 'å': 'a', 'æ': 'ae', 'ā': 'a', 'ą': 'a',
    'ë': 'e', 'ē': 'e', 'ė': 'e', 'ę': 'e', 'ě': 'e',
    'ï': 'i', 'î': 'i', 'ī': 'i', 'į': 'i',
    'ö': 'o', 'ø': 'o', 'ō': 'o', 'ő': 'o', 'œ': 'oe',
    'ü': 'u', 'û': 'u', 'ū': 'u', 'ů': 'u', 'ű': 'u', 'ų': 'u',
    'ÿ': 'y', 'ȳ': 'y',
    'ç': 'c', 'č': 'c', 'ć': 'c', 'ċ': 'c',
    'ñ': 'n', 'ň': 'n', 'ń': 'n', 'ņ': 'n',
    'š': 's', 'ś': 's', 'ş': 's', 'ș': 's',
    'ž': 'z', 'ź': 'z', 'ż': 'z',
    'ł': 'l', 'ļ': 'l', 'ľ': 'l',
    'ř': 'r', 'ŕ': 'r',
    'ţ': 't', 'ț': 't', 'ť': 't',
    'ğ': 'g', 'ģ': 'g',
    'ķ': 'k',
    'ß': 'ss',
    
    // Upper case versions
    'À': 'a', 'Á': 'a', 'Â': 'a', 'Ã': 'a', 'Ä': 'a', 'Å': 'a', 'Æ': 'ae', 'Ā': 'a', 'Ă': 'a', 'Ą': 'a',
    'È': 'e', 'É': 'e', 'Ê': 'e', 'Ë': 'e', 'Ē': 'e', 'Ė': 'e', 'Ę': 'e', 'Ě': 'e',
    'Ì': 'i', 'Í': 'i', 'Î': 'i', 'Ï': 'i', 'Ī': 'i', 'Į': 'i',
    'Ò': 'o', 'Ó': 'o', 'Ô': 'o', 'Õ': 'o', 'Ö': 'o', 'Ø': 'o', 'Ō': 'o', 'Ő': 'o', 'Œ': 'oe',
    'Ù': 'u', 'Ú': 'u', 'Û': 'u', 'Ü': 'u', 'Ū': 'u', 'Ů': 'u', 'Ű': 'u', 'Ų': 'u',
    'Ÿ': 'y', 'Ȳ': 'y',
    'Ç': 'c', 'Č': 'c', 'Ć': 'c', 'Ċ': 'c',
    'Ñ': 'n', 'Ň': 'n', 'Ń': 'n', 'Ņ': 'n',
    'Š': 's', 'Ś': 's', 'Ş': 's', 'Ș': 's',
    'Ž': 'z', 'Ź': 'z', 'Ż': 'z',
    'Ł': 'l', 'Ļ': 'l', 'Ľ': 'l',
    'Ř': 'r', 'Ŕ': 'r',
    'Ţ': 't', 'Ț': 't', 'Ť': 't',
    'Ğ': 'g', 'Ģ': 'g',
    'Ķ': 'k'
  }
  
  // Replace accented characters
  for (const [accented, plain] of Object.entries(accents)) {
    slug = slug.replace(new RegExp(accented, 'g'), plain)
  }
  
  // Keep dots, numbers, apostrophes, and other important punctuation for team names
  // Remove only problematic characters for URLs
  slug = slug.replace(/[^a-z0-9\s\-\.'&]/g, '')
  
  // Replace multiple spaces with single space
  slug = slug.replace(/\s+/g, ' ')
  
  // Replace spaces with hyphens but preserve dots and apostrophes
  slug = slug.replace(/\s/g, '-')
  
  // Clean up multiple consecutive hyphens
  slug = slug.replace(/-+/g, '-')
  
  // Remove leading/trailing hyphens
  slug = slug.trim().replace(/^-+|-+$/g, '')
  
  return slug
}

export function generateMatchSlug(teamHome: string, teamAway: string, league?: string, timeStart?: string, caster?: string): string {
  const homeSlug = sanitizeSlug(teamHome)
  const awaySlug = sanitizeSlug(teamAway)
  const dateTime = timeStart ? formatDateTimeForSlug(timeStart) : ''
  
  let slug = `${homeSlug}-vs-${awaySlug}`
  
  if (dateTime) {
    slug += `-${dateTime}`
  }
  
  // Add caster to distinguish different BLV for same match
  if (caster && caster.trim() !== '') {
    const casterSlug = sanitizeSlug(caster.trim())
    slug += `-blv-${casterSlug}`
  }
  
  return slug
}

export function formatDateTimeForSlug(timeStart: string): string {
  try {
    // If the timeStart is in ISO format, parse it directly
    // If it's already in local timezone format, use it as is
    let date: Date
    
    if (timeStart.includes('T') && timeStart.includes('Z')) {
      // This is UTC time, but we want to treat it as local time for display
      // So we'll parse it as if it's local time
      const isoString = timeStart.replace('Z', '')
      date = new Date(isoString)
    } else {
      date = new Date(timeStart)
    }
    
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    
    return `luc-${hours}${minutes}-ngay-${day}-${month}-${year}`
  } catch (error) {
    return ''
  }
}

export function formatDateForSlug(timeStart: string): string {
  try {
    const date = new Date(timeStart)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  } catch (error) {
    return ''
  }
}

export function parseSlugToMatchInfo(slug: string): {
  teamHome?: string
  teamAway?: string
  league?: string
  date?: string
  time?: string
  caster?: string
} {
  const parts = slug.split('-vs-')
  if (parts.length !== 2) return {}
  
  const [homeTeam, awayAndRest] = parts
  
  // First check for BLV pattern at the end
  let caster = ''
  let restWithoutBLV = awayAndRest
  const blvPattern = /-blv-(.+)$/
  const blvMatch = awayAndRest.match(blvPattern)
  
  if (blvMatch) {
    caster = blvMatch[1].replace(/-/g, ' ')
    restWithoutBLV = awayAndRest.replace(blvPattern, '')
  }
  
  // Try to find datetime pattern (luc-HHMM-ngay-DD-MM-YYYY or vao-luc-HHMM-ngay-DD-MM-YYYY) first
  const dateTimePattern1 = /^(.*?)-luc-(\d{4})-ngay-(\d{2})-(\d{2})-(\d{4})$/
  const dateTimePattern2 = /^(.*?)-vao-luc-(\d{4})-ngay-(\d{2})-(\d{2})-(\d{4})$/
  
  let dateTimeMatch = restWithoutBLV.match(dateTimePattern1) || restWithoutBLV.match(dateTimePattern2)
  
  let awayTeam = ''
  let date = ''
  let time = ''
  
  if (dateTimeMatch) {
    // Found datetime pattern
    const [, awayTeamPart, timeStr, day, month, year] = dateTimeMatch
    awayTeam = awayTeamPart
    time = `${timeStr.substring(0, 2)}:${timeStr.substring(2, 4)}`
    date = `${day}-${month}-${year}`
  } else {
    // Try old date pattern (team-away-dd-mm-yyyy)
    const datePattern = /^(.*?)-(\d{2}-\d{2}-\d{4})$/
    const dateMatch = restWithoutBLV.match(datePattern)
    
    if (dateMatch) {
      const [, awayTeamPart, datePart] = dateMatch
      awayTeam = awayTeamPart
      date = datePart
    } else {
      // No date pattern, entire part is away team
      awayTeam = restWithoutBLV
    }
  }
  
  return {
    teamHome: homeTeam.replace(/-/g, ' '),
    teamAway: awayTeam.replace(/-/g, ' '),
    league: '', // No league in slug anymore
    date,
    time,
    caster
  }
}

export function generateMatchUrl(matchId: string | number, teamHome: string, teamAway: string, league?: string, timeStart?: string, caster?: string): string {
  const slug = generateMatchSlug(teamHome, teamAway, league, timeStart, caster)
  // Properly encode the slug to handle special characters
  const encodedSlug = encodeURIComponent(slug)
  return `/truc-tiep/${encodedSlug}`  // Clean URL with proper encoding
}

// Server-side function to find matchId by slug
export async function findMatchIdBySlug(slug: string): Promise<string | null> {
  try {
    // Decode the slug to handle URL-encoded special characters
    const decodedSlug = decodeURIComponent(slug)
    
    // Parse slug to get team names
    const slugInfo = parseSlugToMatchInfo(decodedSlug);
    
    // Search by team names (most reliable method)
    if (slugInfo.teamHome && slugInfo.teamAway) {
      // Try exact search first
      const searchParams = new URLSearchParams({
        teamHome: slugInfo.teamHome,
        teamAway: slugInfo.teamAway,
        date: slugInfo.date || '',
        time: slugInfo.time || ''
      })
      
      // Add caster to search if available
      if (slugInfo.caster) {
        searchParams.append('caster', slugInfo.caster)
      }
      
      let searchResponse = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000'}/api/matches/search?` + searchParams);
      
      let searchData = await searchResponse.json();
      if (searchData.success && searchData.data && searchData.data.length > 0) {
        return searchData.data[0].id.toString();
      }
      
      // If no exact match, try partial search with first words only
      const homeWords = slugInfo.teamHome.split(' ');
      const awayWords = slugInfo.teamAway.split(' ');
      
      searchResponse = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000'}/api/matches/search?` + new URLSearchParams({
        teamHome: homeWords[0], // First word only
        teamAway: awayWords[0], // First word only
        date: slugInfo.date || '',
        time: slugInfo.time || ''
      }));
      
      searchData = await searchResponse.json();
      if (searchData.success && searchData.data && searchData.data.length > 0) {
        // Filter results to find best match
        const matches = searchData.data;
        
        // Look for exact team name match (case insensitive)
        const exactMatch = matches.find((match: any) => {
          const dbHome = match.team_hone?.toLowerCase().replace(/[-\s]/g, '');
          const dbAway = match.team_away?.toLowerCase().replace(/[-\s]/g, '');
          const slugHome = slugInfo.teamHome?.toLowerCase().replace(/[-\s]/g, '') || '';
          const slugAway = slugInfo.teamAway?.toLowerCase().replace(/[-\s]/g, '') || '';
          
          return dbHome.includes(slugHome) && dbAway.includes(slugAway);
        });
        
        if (exactMatch) {
          return exactMatch.id.toString();
        }
        
        // Return first match as fallback
        return matches[0].id.toString();
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error finding match by slug:', error);
    return null;
  }
} 